/** @jsxImportSource @emotion/react */
import {
  Button,
  Container,
  Form,
  Input,
  Link,
  List,
  Message,
  RadioGroup,
  Stack,
  Text,
} from "basis";
import { AccountInfo } from "library/hooks/types";
import { yesNoOptions } from "library/sharedOptions";
import { capitaliseEveryWord } from "library/utils";
import { CreditCard } from "../CreditCard/CreditCard";
import { ResponsiveRow } from "../ResponsiveRow/ResponsiveRow";
import { CreditLimitDetails } from "./components/CreditLimitDetails";
import { ActionType, ScreenProps, UserAddress } from "./types";
import { getMinMonthlyPayment } from "./utils";
import { validateNewCreditLimit } from "./validation";

export const NewCreditLimit = ({ state, dispatch }: ScreenProps) => {
  const accountInfo = state.accountInfo as AccountInfo;
  const userAddress = state.userAddress as UserAddress;
  const initialValues = {
    contactInformationCorrect: state.contactInformationCorrect,
    newCreditLimit: state.newCreditLimit,
  };
  const onSubmit = ({ values, errors }) => {
    if (
      Object.keys(errors).length === 0 &&
      values.contactInformationCorrect === "yes"
    ) {
      dispatch({
        type: ActionType.NEW_CREDIT_LIMIT_SUBMITTED,
        contactInformationCorrect: values.contactInformationCorrect,
        newCreditLimit: values.newCreditLimit,
        minimumMonthlyPayment: getMinMonthlyPayment({
          creditLimit: values.newCreditLimit,
          minMonthlyPaymentPercentage: accountInfo.cardMmpPercentage,
          minMonthlyPaymentThreshold: accountInfo.cardMmpThreshold,
        }),
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values, errors } }) => (
        <Stack gap="8" flatten>
          <Container bg="grey.t05" padding="4">
            <CreditCard
              cardName={accountInfo.cardName}
              cardEndsWith={accountInfo.cardEndsWith}
            />
          </Container>
          <Text as="h3" textStyle="heading4">
            Set a limit that makes you feel comfortable
          </Text>
          <Container bg="secondary.lightBlue.t25" padding="4">
            <Container bg="white" padding="6">
              <Text>
                <strong>
                  Here’s some important information you’ll need to know about
                  increasing your credit limit.
                </strong>
              </Text>
              <List margin="6 0 0 0">
                <List.Item>
                  The application only takes a few minutes, we’ll be doing a
                  credit check, and if approved your credit will be available
                  instantly.
                </List.Item>
                <List.Item>
                  You might need to provide proof of your income, this could
                  include bank statements, payslips, or a notice of assessment.
                </List.Item>
                <List.Item>
                  <Link
                    href="#"
                    newTab={false}
                    onClick={(e) => {
                      // TODO: basis should provide <Button variant="link">
                      e.preventDefault();
                      dispatch({
                        type: ActionType.NEW_CREDIT_LIMIT_IMPORTANT_INFORMATION_CLICKED,
                        contactInformationCorrect:
                          values.contactInformationCorrect,
                        newCreditLimit: values.newCreditLimit,
                      });
                    }}
                  >
                    Read more important information about increasing your credit
                    limit.
                  </Link>
                </List.Item>
              </List>
            </Container>
          </Container>
          <Text color="primary.blue.t100" textStyle="heading5">
            Firstly, we need to confirm a few details…
          </Text>
          <Container hasBorder padding="4">
            <Stack gap="12">
              <Stack gap="8">
                <Text textStyle="subtitle2">
                  <strong>Contact information</strong>
                </Text>
                <ResponsiveRow>
                  <Text>Email address</Text>
                  <Text>
                    <strong>{accountInfo.userEmail}</strong>
                  </Text>
                </ResponsiveRow>
                <ResponsiveRow>
                  <Text>Mobile phone number</Text>
                  <Text>
                    <strong>{state.userMobilePhoneNumber ?? "–"}</strong>
                  </Text>
                </ResponsiveRow>
                <ResponsiveRow>
                  <Text>Home phone number</Text>
                  <Text>
                    <strong>{state.userHomePhoneNumber ?? "–"}</strong>
                  </Text>
                </ResponsiveRow>
                <ResponsiveRow>
                  <Text>Work phone number</Text>
                  <Text>
                    <strong>{state.userWorkPhoneNumber ?? "–"}</strong>
                  </Text>
                </ResponsiveRow>
                <ResponsiveRow>
                  <Text>Residential address</Text>
                  <Text>
                    <strong>
                      {capitaliseEveryWord(userAddress.address1)}
                      <br />
                      {userAddress.address2 && (
                        <>
                          {capitaliseEveryWord(userAddress.address2)}
                          <br />
                        </>
                      )}
                      {`${capitaliseEveryWord(userAddress.city)}, ${
                        userAddress.post_code
                      }`}
                      <br />
                      {`${userAddress.state.toUpperCase()}, ${capitaliseEveryWord(
                        userAddress.country,
                      )}`}
                    </strong>
                  </Text>
                </ResponsiveRow>
              </Stack>
              <Container width="100%" width-sm="400">
                <RadioGroup
                  name="contactInformationCorrect"
                  label="Is the information above correct?"
                  options={yesNoOptions}
                />
              </Container>
              {values.contactInformationCorrect === "no" && (
                <Message
                  severity="info-or-minor"
                  bg="secondary.lightBlue.t25"
                  title="Need to update this information?"
                >
                  You can update your details on the{" "}
                  <Link
                    href="https://latitudefs.zendesk.com/hc/en-au/articles/360007747097-Update-your-details"
                    newTab
                  >
                    Latitude website
                  </Link>
                  .
                </Message>
              )}
            </Stack>
          </Container>
          {values.contactInformationCorrect === "yes" && (
            <>
              <Text as="h3" textStyle="heading4">
                What limit did you have in mind?
              </Text>
              <Text>
                Please choose a limit based on your planned purchases and one
                that suits your revisited requirements.
              </Text>
              <Container width="280">
                <Input
                  name="newCreditLimit"
                  label="New credit limit"
                  maxLength="5"
                  variant="numeric"
                  prefix="$"
                  validate={validateNewCreditLimit}
                  validateData={{
                    creditLimit: accountInfo.creditLimit,
                    maxCreditLimit: accountInfo.cardMaxCreditLimit,
                  }}
                />
              </Container>
              <CreditLimitDetails
                newCreditLimit={values.newCreditLimit}
                accountInfo={accountInfo}
                errors={errors}
              />

              <Button type="submit" width="192">
                Start request
              </Button>
            </>
          )}
        </Stack>
      )}
    </Form>
  );
};
